<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="mb-1 block text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </label>
    <div class="relative rounded-md">
      <div
        class="absolute inset-y-0 left-0 flex w-10 items-center justify-center rounded-l-md border-r border-background-fg/8 text-background-fg"
      >
        <span class="font-semibold">
          {{ getCurrencySymbol(tenantCurrency) }}
        </span>
      </div>
      <input
        ref="inputRef"
        :disabled="disabled"
        type="text"
        :name="name"
        :class="[
          'block w-full rounded-md border border-background-fg/8 pl-12 text-base font-semibold focus:outline-none', // Font size set at >14px (text-base) so no zoom on mobile when focused
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
        :placeholder="placeholder"
        :value="disabled ? '' : formattedValue"
        :aria-invalid="hasError"
        enterKeyHint="done"
        @blur="handleBlur"
        @keydown.backspace="handleBackspace"
        @keydown.enter="handleEnter"
      />
    </div>
    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useCurrency } from "@/composables/useCurrency"
import { useField } from "vee-validate"
import {
  useCurrencyInput,
  CurrencyDisplay,
  ValueScaling,
} from "vue-currency-input"

const props = withDefaults(
  defineProps<{
    name?: string
    label?: string
    placeholder?: string
    currency?: string
    disabled?: boolean
    min?: number
    max?: number
  }>(),
  {
    name: "",
    label: "",
    placeholder: "",
    currency: "GBP",
    disabled: false,
    min: undefined,
    max: undefined,
  }
)

defineModel<number | undefined>()

const {
  numberValue: internalValueNumber,
  inputRef,
  setValue: setInternalValue,
  formattedValue,
} = useCurrencyInput(
  {
    currency: props.currency,
    currencyDisplay: CurrencyDisplay.hidden,
    autoDecimalDigits: true,
    hideCurrencySymbolOnFocus: false,
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    valueScaling: ValueScaling.precision,
    valueRange: {
      min: props.min,
      max: props.max,
    },
  },
  false
)

const {
  value: fieldValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField<number | undefined>(
  toRef(props, "name"),
  undefined,
  { syncVModel: true }
)

const { getCurrencySymbol } = useCurrency()
const { currency: tenantCurrency } = useTenant()

watch(
  [fieldValue, inputRef],
  ([newFieldValue]) => {
    if (newFieldValue !== undefined) {
      setInternalValue(newFieldValue)
    }
  },
  { immediate: true }
)

const hasError = computed(() => !!errorMessage.value)

watch(internalValueNumber, (newValue) => {
  if (newValue === null) {
    handleChange(0)
    return
  }

  handleChange(newValue)
})

const handleBackspace = (event: KeyboardEvent) => {
  if (internalValueNumber.value === 0) {
    event.preventDefault()
  }
}

const handleEnter = (event: Event) =>
  (event?.target as HTMLInputElement)?.blur()
</script>
